import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Chip, Divider, List, ListItemButton, Paper, Typography } from '@mui/material';
import { IOrganisationEventItem, IOrganisationEventsResponse } from '../../types/@organisation';

interface IProps {
  events: IOrganisationEventsResponse;
}

const getEventCardStyles = (status: IOrganisationEventItem['status']) => {
  if (status === 'live') {
    return {
      background: '#eaf4ff',
      border: '1px solid #9bc7ff',
      accent: '#1565c0',
      text: '#0e3d73',
    };
  }

  if (status === 'finished') {
    return {
      background: '#f1f3f5',
      border: '1px solid #d7dce1',
      accent: '#6c757d',
      text: '#4f5b66',
    };
  }

  return {
    background: '#ffffff',
    border: '1px solid #e3e8ef',
    accent: '#8c99a5',
    text: '#17324d',
  };
};

const formatDate = (value: number | null): string => {
  if (!value) return 'Start time not set';

  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Start time unavailable';
  }
};

const resolveServiceEventId = (event: any): string => {
  return String(event?.eventId || event?._id || event?.id || event?.qdosEventId || '').trim();
};

const OrganisationEventsPanel = ({ events }: IProps): ReactElement => {
  const [selectedTeamId, setSelectedTeamId] = useState<string>(events.teams[0]?.teamId || '');
  const navigate = useNavigate();

  const selectedTeam = useMemo(() => {
    return events.teams.find((team) => team.teamId === selectedTeamId) || events.teams[0] || null;
  }, [events.teams, selectedTeamId]);

  const handleCreateRepeatEvent = (): void => {
    const query = new URLSearchParams({
      organisationId: events.organisationId,
    });

    if (selectedTeam?.teamId) {
      query.set('teamId', selectedTeam.teamId);
    }

    navigate(`/events/create?${query.toString()}`);
  };

  const handleCreateNewStyleEvent = (): void => {
    const query = new URLSearchParams({
      organisationId: events.organisationId,
      mode: 'custom',
    });

    if (selectedTeam?.teamId) {
      query.set('teamId', selectedTeam.teamId);
    }

    navigate(`/events/create?${query.toString()}`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>Events</Typography>
        <Chip label={`Teams: ${events.summary.teamCount}`} />
        <Chip label={`Live: ${events.summary.liveEvents}`} color="primary" />
        <Chip label={`Upcoming: ${events.summary.upcomingEvents}`} />
        <Chip label={`Finished: ${events.summary.finishedEvents}`} />
      </Box>

      {!events.teams.length ? (
        <Alert severity="info">No organisation events are currently available.</Alert>
      ) : (
        <>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ borderRadius: '16px', px: 2, py: 2, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
          >
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#17324d' }}>
                Repeat-event flow
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
                Most organisations only change the team and sponsor setup. Start there by default.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handleCreateRepeatEvent}>
                Create event
              </Button>
              <Button variant="outlined" onClick={handleCreateNewStyleEvent}>
                New style event
              </Button>
            </Box>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 2, background: '#fafbfd' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#17324d' }}>Teams</Typography>
                <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mt: 0.5 }}>
                  Select a team to see its event list and create the next repeat event quickly.
                </Typography>
              </Box>

              <Divider />

              <List disablePadding>
                {events.teams.map((team) => {
                  const active = selectedTeam?.teamId === team.teamId;

                  return (
                    <ListItemButton
                      key={team.teamId}
                      selected={active}
                      onClick={() => setSelectedTeamId(team.teamId)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1,
                        px: 2,
                        py: 1.75,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#17324d' }}>{team.teamName}</Typography>
                        <Chip size="small" label={team.events.length} />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {team.counts.live > 0 && <Chip size="small" color="primary" label={`Live ${team.counts.live}`} />}
                        {team.counts.upcoming > 0 && <Chip size="small" label={`Upcoming ${team.counts.upcoming}`} />}
                        {team.counts.finished > 0 && <Chip size="small" label={`Finished ${team.counts.finished}`} />}
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedTeam ? (
                <>
                  <Box>
                    <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#17324d' }}>
                      {selectedTeam.teamName}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
                      Tap an event to open the smartphone service panel.
                    </Typography>
                  </Box>

                  {selectedTeam.events.length ? (
                    selectedTeam.events.map((event) => {
                      const basePalette = getEventCardStyles(event.status);
                      const palette = event.isActive
                        ? {
                            background: '#eefbf3',
                            border: '2px solid #2e7d32',
                            accent: '#2e7d32',
                            text: '#124c2f',
                          }
                        : basePalette;
                      const serviceEventId = resolveServiceEventId(event);

                      return (
                        <ButtonBase
                          key={event.eventId || event._id || event.id || event.name}
                          onClick={() => {
                            if (!serviceEventId) {
                              return;
                            }

                            navigate(`/events/${serviceEventId}/service`);
                          }}
                          sx={{ display: 'block', textAlign: 'left', borderRadius: '16px' }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              width: '100%',
                              p: 2.25,
                              borderRadius: '16px',
                              background: palette.background,
                              border: palette.border,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', md: 'center' },
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography
                                  sx={{
                                    fontSize: '18px',
                                    fontWeight: event.isActive ? 800 : 700,
                                    color: palette.text,
                                  }}
                                >
                                  {event.name}
                                </Typography>
                                {event.isActive ? (
                                  <Chip
                                    label="ACTIVE"
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 800 }}
                                  />
                                ) : null}
                              </Box>
                              <Chip
                                label={event.statusLabel}
                                size="small"
                                sx={{
                                  background: '#fff',
                                  color: palette.accent,
                                  border: `1px solid ${palette.accent}`,
                                  fontWeight: 700,
                                }}
                              />
                            </Box>

                            {event.slogan ? (
                              <Typography sx={{ fontSize: '14px', color: '#53657a' }}>{event.slogan}</Typography>
                            ) : null}

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              <Chip size="small" label={event.gameType || 'Unknown game'} />
                              <Chip size="small" label={formatDate(event.eventStartTime)} />
                              {event.sponsorPrice ? (
                                <Chip size="small" label={`${event.sponsorPrice} ${event.sponsorCurrency || ''}`.trim()} />
                              ) : null}
                            </Box>
                          </Paper>
                        </ButtonBase>
                      );
                    })
                  ) : (
                    <Alert severity="info">No events have been created for this team yet.</Alert>
                  )}
                </>
              ) : (
                <Alert severity="info">Select a team to review its event list.</Alert>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default OrganisationEventsPanel;
